<template lang="html">
  <div
    class="cart-item flex flex-wrap items-center gap-4 border border-neutral-200 p-2 max-sm:gap-2"
  >
    <UCheckbox
      v-if="props.selectable"
      :model-value="props.selected"
      class="mr-2 self-start"
      size="xl"
      @update:model-value="emit('update:selected', $event === true)"
    />

    <NuxtLink :to="link" class="cart-item__cover h-25 w-25">
      <UiPicture :src="cover?.id" variant="imageFull" />
    </NuxtLink>

    <div class="cart-item__info flex flex-col gap-1 self-start py-2">
      <NuxtLink :to="link" class="text-sm font-bold hover:underline">
        {{ props.value?.wellKnownAttributes?.Name || 'Без названия' }}
      </NuxtLink>

      <div class="flex flex-col gap-1">
        <PromoBadge
          :badge="props.value?.promoBadge"
          :cart-promo="props.value?.hasCartPromo && !props.value?.hasCashbackPromo"
          :cashback="props.value?.hasCashbackPromo"
        />
        <PromoPrice
          v-if="props.value"
          :value="props.value"
          :unit-price="props.unitPrice"
          class="text-sm"
        />
        <span v-if="!props.value?.inStock" class="text-sm font-bold text-muted">
          * Нет в наличии
        </span>
      </div>

      <template v-if="keyAttrs">
        <div
          v-for="(attr, index) in keyAttrs"
          :key="index"
          class="flex flex-wrap gap-2"
        >
          <span>{{ attr.displayName }}:</span>
          <span>{{ attr.value }}</span>
        </div>
      </template>
    </div>

    <div
      class="cart-item__actions ml-auto flex justify-end gap-1 max-sm:w-full"
    >
      <UButton
        v-if="props.showNotify && !props.value?.inStock"
        :icon="
          props.value?.notifyInStock
            ? 'i-heroicons-bell-solid'
            : 'i-lucide-bell'
        "
        variant="ghost"
        :class="props.value?.notifyInStock ? 'text-tertiary-500' : 'text-muted'"
        @click="emit('toggleNotify', props.value!.id)"
      />

      <UButton
        icon="i-lucide-trash"
        variant="ghost"
        class="text-muted"
        @click="handleRemove"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CartProductListItemDto } from '@repo/api/web/models'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import PromoBadge from '#Promotion/components/PromoBadge.vue'
import PromoPrice from '#Promotion/components/PromoPrice.vue'
import UiPicture from '~/app/components/Image/UiPicture.vue'

interface AttributeMeta {
  displayName?: string
  systemName?: string
  defaultConfig?: { type?: string }
}

const props = withDefaults(
  defineProps<{
    value?: CartProductListItemDto
    attributesMeta?: Record<string, object>
    selectable?: boolean
    selected?: boolean
    showNotify?: boolean
    unitPrice?: number
  }>(),
  {
    selectable: false,
    selected: false,
    showNotify: false,
  },
)
const emit = defineEmits<{
  remove: [productId: number]
  'update:selected': [value: boolean]
  toggleNotify: [productId: number]
}>()

const { routing } = useProducts()
const ecommerce = useEcommerce()

// Ecommerce: удаление из корзины (remove) — здесь доступен полный DTO позиции
function handleRemove() {
  if (!props.value) return
  ecommerce.remove(
    mapCatalogProduct(props.value, { quantity: props.value.amount || 1 }),
  )
  emit('remove', props.value.id)
}

const cover = computed(() => {
  return props.value?.images?.at(0)
})
const link = computed(() => {
  return routing.getDetails(props.value as any)?.route
})
const keyAttrs = computed(() => {
  if (!props.value?.keyAttributesIds?.length) return

  return props.value.keyAttributesIds
    .map((id) => {
      const attrValues = props.value?.allAttributes?.[String(id)]
      const meta = props.attributesMeta?.[String(id)] as
        | AttributeMeta
        | undefined
      if (!attrValues?.length || !meta) return null

      const firstValue = attrValues[0]
      const displayValue = firstValue.displayName ?? firstValue.value

      return {
        displayName: meta.displayName as string,
        value: displayValue,
      }
    })
    .filter((item): item is NonNullable<typeof item> => item != null)
})
</script>
